/**
 * CZNA-684 — ask the backend which control fields it will supply for a record the
 * cataloguer has not saved yet.
 *
 * The MARC preview next to the proposal form is the only place a cataloguer sees the
 * record before it exists, and it used to show a record without the fields the system
 * fills in — so the preview and the saved record disagreed, and the one field in that
 * set that is genuinely derivable (`008`) was missing from the save as well, which is
 * what made every form-made proposal unsubmittable.
 *
 * Fetched ONCE per (type, proposal) and never invalidated: `staleTime: Infinity` plus a
 * key that does not carry form state. It can be: the derived `008` for a new authority
 * is a function of the record type and the day, and neither moves while the form is
 * open. Keying it on the live form state would put a request on the debounce cycle of
 * every keystroke, which is what a live preview must never do.
 *
 * `ky` is captured by the query closure and deliberately kept out of any dependency
 * array (project MEMORY: `useOkapiKy()` returns a new instance per render).
 */
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { deriveControlFields } from '../api/validationService';
export var DERIVED_CONTROL_FIELDS_QUERY_KEY = 'czna-derived-control-fields';
/**
 * @param recordType  the authority type being edited — the derivation's rule selector
 * @param buildRecord builds the MARC payload to derive from; called at most once per
 *                    cache key, so it may read render-scoped values
 * @param cacheKey    stable identity of the thing being edited (a proposal id, or
 *                    `'new'`) — the second half of the query key
 * @param enabled     `false` skips the request entirely
 */
export var useDerivedControlFields = function (recordType, buildRecord, cacheKey, enabled) {
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    return useQuery([DERIVED_CONTROL_FIELDS_QUERY_KEY, recordType, cacheKey], function () { return deriveControlFields(ky, {
        record: buildRecord(),
        recordType: recordType,
        // Ignored by the derive path; the request type requires it.
        operation: 'CREATE_DRAFT',
    }); }, {
        enabled: enabled,
        staleTime: Infinity,
        // A preview enrichment must never retry-storm or surface an error: without it
        // the panel simply shows what it showed before CZNA-684.
        retry: false,
    });
};
